import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  User,
  Mail,
  Briefcase,
  Image,
  Award,
  Trophy,
  Lock,
  Zap,
  Building2,
  UserCheck,
  Plus,
  Edit,
  Trash2,
  TestTube,
} from 'lucide-react';

interface PermissionRoles {
  employee: { view: boolean; edit: boolean };
  manager: { view: boolean; edit: boolean };
  hr: { view: boolean; edit: boolean };
  admin: { view: boolean; edit: boolean };
}

interface PermissionSection {
  label: string;
  description: string;
  icon: string;
  sections: string[];
  roles: PermissionRoles;
}

interface Permissions {
  [key: string]: PermissionSection;
}

interface DepartmentRestriction {
  restrictedSections: string[];
  requiresApproval: boolean;
}

interface DepartmentRestrictions {
  [key: string]: DepartmentRestriction;
}

interface Role {
  id: string;
  label: string;
  color: string;
  description: string;
}

interface PermissionMatrixTabProps {
  onSettingsChange: () => void;
  isTestMode?: boolean;
}

const PermissionMatrixTab = ({ onSettingsChange, isTestMode }: PermissionMatrixTabProps) => {
  const { t } = useTranslation();

  const [permissions, setPermissions] = useState<Permissions>({
    basicInfo: {
      label: 'Basic Information',
      description: 'Name, job title, department, location',
      icon: 'User',
      sections: ['firstName', 'lastName', 'jobTitle', 'department', 'location'],
      roles: {
        employee: { view: true, edit: true },
        manager: { view: true, edit: true },
        hr: { view: true, edit: true },
        admin: { view: true, edit: true },
      },
    },
    contactInfo: {
      label: 'Contact Information',
      description: 'Email, phone number, social links',
      icon: 'Mail',
      sections: ['email', 'phone', 'linkedIn', 'github'],
      roles: {
        employee: { view: true, edit: true },
        manager: { view: true, edit: false },
        hr: { view: true, edit: true },
        admin: { view: true, edit: true },
      },
    },
    professionalInfo: {
      label: 'Professional Summary',
      description: 'Bio, professional summary, career highlights',
      icon: 'Briefcase',
      sections: ['bio', 'summary', 'highlights'],
      roles: {
        employee: { view: true, edit: true },
        manager: { view: true, edit: false },
        hr: { view: true, edit: false },
        admin: { view: true, edit: true },
      },
    },
    photo: {
      label: 'Profile Photo',
      description: 'Profile picture and avatar',
      icon: 'Image',
      sections: ['profilePhoto'],
      roles: {
        employee: { view: true, edit: true },
        manager: { view: true, edit: false },
        hr: { view: true, edit: true },
        admin: { view: true, edit: true },
      },
    },
    skillsListing: {
      label: 'Skills & Expertise',
      description: 'Technical skills, certifications, expertise areas',
      icon: 'Award',
      sections: ['skills', 'certifications', 'expertise'],
      roles: {
        employee: { view: true, edit: true },
        manager: { view: true, edit: true },
        hr: { view: true, edit: false },
        admin: { view: true, edit: true },
      },
    },
    achievements: {
      label: 'Achievements & Awards',
      description: 'Recognition, awards, milestones',
      icon: 'Trophy',
      sections: ['awards', 'recognition', 'milestones'],
      roles: {
        employee: { view: true, edit: false },
        manager: { view: true, edit: true },
        hr: { view: true, edit: true },
        admin: { view: true, edit: true },
      },
    },
    privacySettings: {
      label: 'Privacy & Visibility',
      description: 'Public visibility controls, QR code access',
      icon: 'Lock',
      sections: ['isPublic', 'qrCodeEnabled', 'visibilitySettings'],
      roles: {
        employee: { view: true, edit: true },
        manager: { view: true, edit: false },
        hr: { view: true, edit: true },
        admin: { view: true, edit: true },
      },
    },
  });

  const [departmentRestrictions, setDepartmentRestrictions] = useState<DepartmentRestrictions>({
    finance: { restrictedSections: ['contactInfo'], requiresApproval: true },
    legal: { restrictedSections: ['contactInfo', 'photo'], requiresApproval: true },
    executive: { restrictedSections: ['contactInfo', 'privacySettings'], requiresApproval: true },
  });

  const roles: Role[] = [
    { id: 'employee', label: 'Employee', color: 'blue', description: 'Standard employee access' },
    { id: 'manager', label: 'Manager', color: 'green', description: 'Team manager access' },
    { id: 'hr', label: 'HR', color: 'purple', description: 'HR administrator access' },
    { id: 'admin', label: 'Admin', color: 'red', description: 'System administrator access' },
  ];

  const handlePermissionChange = (section: string, role: string, permission: string, value: boolean) => {
    setPermissions((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        roles: {
          ...prev[section].roles,
          [role]: {
            ...prev[section].roles[role as keyof PermissionRoles],
            [permission]: value,
          },
        },
      },
    }));
    onSettingsChange();
  };

  const handleBulkUpdate = (role: string, permission: string, value: boolean) => {
    const updatedPermissions = { ...permissions };
    Object.keys(updatedPermissions).forEach((section) => {
      (updatedPermissions[section].roles[role as keyof PermissionRoles] as any)[permission] = value;
    });
    setPermissions(updatedPermissions);
    onSettingsChange();
  };

  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'User':
        return <User className="w-5 h-5 text-gray-600" />;
      case 'Mail':
        return <Mail className="w-5 h-5 text-gray-600" />;
      case 'Briefcase':
        return <Briefcase className="w-5 h-5 text-gray-600" />;
      case 'Image':
        return <Image className="w-5 h-5 text-gray-600" />;
      case 'Award':
        return <Award className="w-5 h-5 text-gray-600" />;
      case 'Trophy':
        return <Trophy className="w-5 h-5 text-gray-600" />;
      case 'Lock':
        return <Lock className="w-5 h-5 text-gray-600" />;
      default:
        return <User className="w-5 h-5 text-gray-600" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Role Legend */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">{t('Role Definitions')}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {roles.map((role) => (
            <div key={role.id} className="flex items-start">
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-medium bg-${role.color}-100 text-${role.color}-700 mr-2`}
              >
                {t(role.label)}
              </span>
              <p className="text-xs text-gray-600">{t(role.description)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Bulk Actions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Zap className="w-5 h-5 text-blue-600 mr-2" />
            <h4 className="text-sm font-semibold text-blue-900">{t('Bulk Permission Updates')}</h4>
          </div>
          <div className="flex gap-2">
            {roles.map((role) => (
              <div key={role.id} className="flex gap-1">
                <button
                  className="px-2 py-1 text-xs border border-border rounded text-gray-700 hover:bg-gray-50"
                  onClick={() => handleBulkUpdate(role.id, 'view', true)}
                >
                  {t(role.label)} {t('View All')}
                </button>
                <button
                  className="px-2 py-1 text-xs border border-border rounded text-gray-700 hover:bg-gray-50"
                  onClick={() => handleBulkUpdate(role.id, 'edit', false)}
                >
                  {t(role.label)} {t('Edit None')}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Permissions Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-border">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 w-1/4">
                  {t('Profile Section')}
                </th>
                {roles.map((role) => (
                  <th key={role.id} className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                    <div className="flex items-center justify-center">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium bg-${role.color}-100 text-${role.color}-700`}
                      >
                        {t(role.label)}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {Object.entries(permissions).map(([key, section]) => (
                <tr key={key} className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-6 py-4">
                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                        {getIconComponent(section.icon)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{t(section.label)}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{t(section.description)}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {section.sections.map((s) => (
                            <span key={s} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </td>
                  {roles.map((role) => (
                    <td key={role.id} className="px-6 py-4">
                      <div className="flex items-center justify-center space-x-4">
                        {/* View Permission */}
                        <label className="flex items-center cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={section.roles[role.id as keyof PermissionRoles].view}
                            onChange={(e) => handlePermissionChange(key, role.id, 'view', e.target.checked)}
                            className="h-4 w-4 rounded border-border text-blue-600 focus-visible:ring-primary/20"
                          />
                          <span className="ml-1.5 text-xs text-gray-600 group-hover:text-gray-900">
                            {t('View')}
                          </span>
                        </label>

                        {/* Edit Permission */}
                        <label className="flex items-center cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={section.roles[role.id as keyof PermissionRoles].edit}
                            onChange={(e) => handlePermissionChange(key, role.id, 'edit', e.target.checked)}
                            className="h-4 w-4 rounded border-border text-green-600 focus:ring-green-500"
                          />
                          <span className="ml-1.5 text-xs text-gray-600 group-hover:text-gray-900">
                            {t('Edit')}
                          </span>
                        </label>
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Department-Specific Restrictions */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Building2 className="w-5 h-5 text-gray-600 mr-2" />
            <h4 className="text-sm font-semibold text-gray-900">{t('Department-Specific Restrictions')}</h4>
          </div>
          <button className="flex items-center gap-1 px-3 py-1.5 text-sm border border-border rounded-lg text-gray-700 hover:bg-gray-50">
            <Plus className="w-4 h-4" />
            {t('Add Department Rule')}
          </button>
        </div>

        <div className="space-y-3">
          {Object.entries(departmentRestrictions).map(([dept, config]) => (
            <div key={dept} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <Building2 className="w-4 h-4 text-gray-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-gray-900 capitalize">{t(dept)}</p>
                  <p className="text-xs text-gray-500">
                    {t('Restricted')}: {config.restrictedSections.join(', ')} &bull;{' '}
                    {config.requiresApproval ? t('Requires Approval') : t('No Approval Required')}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button className="p-1 text-gray-700 hover:text-gray-900">
                  <Edit className="w-4 h-4" />
                </button>
                <button className="p-1 text-gray-700 hover:text-gray-900">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Manager Delegation Settings */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center mb-4">
          <UserCheck className="w-5 h-5 text-gray-600 mr-2" />
          <h4 className="text-sm font-semibold text-gray-900">{t('Manager Delegation Settings')}</h4>
        </div>

        <div className="space-y-3">
          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
            <div>
              <p className="text-sm font-medium text-gray-900">
                {t('Allow managers to delegate profile edit permissions')}
              </p>
              <p className="text-xs text-gray-500">
                {t('Managers can temporarily grant edit access to team members')}
              </p>
            </div>
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-border text-blue-600 focus-visible:ring-primary/20"
              defaultChecked
            />
          </label>

          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
            <div>
              <p className="text-sm font-medium text-gray-900">
                {t('Require manager approval for profile photo changes')}
              </p>
              <p className="text-xs text-gray-500">
                {t('All profile photo updates must be approved by direct manager')}
              </p>
            </div>
            <input type="checkbox" className="h-4 w-4 rounded border-border text-blue-600 focus-visible:ring-primary/20" />
          </label>

          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
            <div>
              <p className="text-sm font-medium text-gray-900">
                {t('Enable cross-department permission inheritance')}
              </p>
              <p className="text-xs text-gray-500">
                {t('Permissions follow employees when they transfer departments')}
              </p>
            </div>
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-border text-blue-600 focus-visible:ring-primary/20"
              defaultChecked
            />
          </label>
        </div>
      </div>

      {isTestMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <TestTube className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-blue-900 mb-1">{t('Test Mode Active')}</h4>
              <p className="text-sm text-blue-800 mb-3">
                {t('Test your permission configurations before applying them. Select a role and section to simulate access.')}
              </p>
              <div className="flex gap-2">
                <button className="px-3 py-1.5 text-sm border border-border rounded-lg text-gray-700 hover:bg-gray-50">
                  {t('Test as Employee')}
                </button>
                <button className="px-3 py-1.5 text-sm border border-border rounded-lg text-gray-700 hover:bg-gray-50">
                  {t('Test as Manager')}
                </button>
                <button className="px-3 py-1.5 text-sm border border-border rounded-lg text-gray-700 hover:bg-gray-50">
                  {t('Test as HR')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionMatrixTab;
